<template>
  <v-card v-if="customer" class="ma-0" :class="fillHeight ? 'fill-height' : ''" width="100%">
    <WarningBanner :warning-messages="customerWarningMessages">
    </WarningBanner>
    <slotted-card :disabled="CustomerReadonly"
      :title="editObject && editObject.Name ? editObject.Name : $t('servicesystem.common_objects.fallbackterms.no_customer_name')"
      :icon="'$customer'" :fontsize="'h3'" :noheaderdivider="true" :externalEditTrigger="edittrigger"
      v-click-outside="unfocusEntireCard" @editMode="setEditMode" @valid="setValid">

      <template v-slot:headertitle>
        <v-row v-if="!editMode" no-gutters align="center">
          <router-link :to="{ name: 'ViewCustomer', params: { customerId: editObject.Id } }"
            class="onlyshowlinkonhover">
            <h3 class="mr-2" :class="{ deactivated: customer && !customer.IsActive }">{{ editObject.Id }} {{
              editObject.Name }}</h3>
          </router-link>
        </v-row>
        <v-row v-else no-gutters align="center">
          <h3 class="mr-2">{{ editObject.Id }}</h3>
          <QTextField v-model="editObject.Name" :label="$t('servicesystem.common_objects.customer.customer_name')"
            :disabled="CustomerReadonly" :externaledit="editMode" singleline
            @FocusState="editMode ? null : edittrigger++" class="h3">
          </QTextField>
        </v-row>
      </template>

      <template v-slot:headerbtns>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn @click="jobsdialogtrigger++" v-bind="attrs" v-on="on" icon color="primary" class="mx-0">
              <v-icon v-text="'mdi-briefcase-search'" />
            </v-btn>
          </template>
          <span v-text="$t('servicesystem.common_objects.customer.other_jobs')" />
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-if="!CustomerReadonly" v-bind="attrs" v-on="on" icon small class="mr-3 ml-1"
              @click="() => { edittrigger++; SetDetails(); }">
              <v-icon :color="editMode ? 'error' : ''">{{ editMode ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ editMode ? $t("servicesystem.text.cancel") : $t("servicesystem.text.edit") }}</span>
        </v-tooltip>
      </template>

      <template v-slot:content>
        <Layout v-model="selectedTemplate" :key="edittrigger">
          <template v-slot:static-content>
            <QTextField v-model="editObject.OrganisationNumber"
              :label="$t('servicesystem.common_objects.customer.organization_number')" :externaledit="editMode"
              :disabled="CustomerReadonly" class="mx-5" @FocusState="editMode ? null : edittrigger++">
            </QTextField>
            <EditLi :label="$t('servicesystem.common_objects.customer.credit_limit')" :disabled="CustomerReadonly"
              :value="customer.CreditLimit ? customer.CreditLimit : $t('servicesystem.common_objects.commonterms.none')"
              :suffix="customer.CreditLimitExceeded ? (' (' + customer.CreditLimitExceeded + ')') : null"
              :valuecolor="customer.CreditLimit || customer.CreditLimitExceeded ? (customer.CreditLimitExceeded ? 'error' : 'primary') : 'disabled'" />
            <QSwitchInput v-model="editObject.FreeFromVat" :disabled="CustomerReadonly"
              :label="$t('servicesystem.common_objects.customer.free_from_vat')" :externaledit="editMode" class="mx-5"
              @FocusState="editMode ? null : edittrigger++">
            </QSwitchInput>
            <ParentCustomerCard :id="customer.ParentId" />
            <edit-bar v-if="!isNew && editMode" :externalTrigger="editMode" :saveable="valid && IsChanged"
              @saveEmit="handleSave" @modeEmit="() => { edittrigger++; SetDetails(); }" :iconmode="true"
              :dividerPos="'none'" class="mt-2">
            </edit-bar>
          </template>

          <template v-slot:option-list>

            <AddressTemplate :Address="address" :hideCountry="!!selectedTemplate" :hideName="!!selectedTemplate"
              :class="selectedTemplate && (selectedTemplate.identifier === identifierkeys.address) ? 'flowinto' : null"
              :loading="loading || SwappingAddress">
              <template v-slot:action-buttons>
                <template
                  v-if="!isOrder && (!selectedTemplate || (selectedTemplate.identifier != identifierkeys.address))">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-if="AllowSwapAddress" v-bind="attrs" v-on="on" icon
                        @click="selectedTemplate = { type: typekeys.list, identifier: identifierkeys.address }">
                        <v-icon size="40">mdi-menu-down</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("servicesystem.text.swap") }}</span>
                  </v-tooltip>
                  <!-- if address exists show edit -->
                  <template v-if="address">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn v-if="!isOrder && AllowChangeAddress" v-bind="attrs" v-on="on" icon
                          @click="selectedTemplate = { type: typekeys.edit, identifier: identifierkeys.address }">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("servicesystem.text.edit") }}</span>
                    </v-tooltip>
                  </template>
                  <!-- else if allowed show create new button -->
                  <template
                    v-else-if="(CustomerAddressCanCreateInErp || CustomerAddressCanCreateLocal) && checkPermission('Actions', 'createCustomerAddress')">
                    <v-btn @click="selectedTemplate = { type: typekeys.new, identifier: identifierkeys.address }" small
                      color="primary">
                      <v-icon size="17" class="mr-1">mdi-plus</v-icon>{{
                        $t('servicesystem.common_objects.commonterms.new')
                      }}
                    </v-btn>
                  </template>
                </template>
              </template>
            </AddressTemplate>

            <v-divider />

            <ContactTemplate :Contact="contact" :hideTitle="!!selectedTemplate" :hideMobile="!!selectedTemplate"
              :class="selectedTemplate && (selectedTemplate.identifier === identifierkeys.contact) ? 'flowinto' : null"
              :loading="loading || SwappingContact">
              <template v-slot:action-buttons>
                <template v-if="!selectedTemplate || (selectedTemplate.identifier != identifierkeys.contact)">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-if="AllowSwapContact" v-bind="attrs" v-on="on" icon
                        @click="selectedTemplate = { type: typekeys.list, identifier: identifierkeys.contact }">
                        <v-icon size="40">mdi-menu-down</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("servicesystem.text.swap") }}</span>
                  </v-tooltip>
                  <!-- if contact exists show edit -->
                  <template v-if="contact">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn v-if="AllowChangeContact" v-bind="attrs" v-on="on" icon
                          @click="selectedTemplate = { type: typekeys.edit, identifier: identifierkeys.contact }">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("servicesystem.text.edit") }}</span>
                    </v-tooltip>
                  </template>
                  <!-- else if allowed show create new button -->
                  <template v-else-if="!CustomerPersonsReadonly && checkPermission('Actions', 'createCustomerContact')">
                    <v-btn @click="selectedTemplate = { type: typekeys.new, identifier: identifierkeys.contact }" small
                      color="primary">
                      <v-icon size="17" class="mr-1">mdi-plus</v-icon>{{
                        $t('servicesystem.common_objects.commonterms.new')
                      }}
                    </v-btn>
                  </template>
                </template>
              </template>
            </ContactTemplate>

            <!--
          <v-divider />

          <JobListMini :class="selectedTemplate && (selectedTemplate.identifier === identifierkeys.job) ? 'flowinto' : null"
                       :CustomerId="customer.Id"
                       :hideDates="!!selectedTemplate">
            <template v-slot:action-buttons>
              <v-btn icon
                     @click="selectedTemplate = { type: typekeys.list, identifier: identifierkeys.job }">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </template>
          </JobListMini>
          -->
          </template>

          <template v-slot:main-content>
            <template v-if="selectedTemplate.identifier === identifierkeys.address">
              <AddressList v-if="selectedTemplate.type === typekeys.list" :CustomerId="customer.Id" :Selected="address"
                @EmitList="e => (addressList = e)"
                @New="selectedTemplate = { type: typekeys.new, identifier: identifierkeys.address }"
                @Select="setAddress" @Close="closeMainContent()">
              </AddressList>
              <AddressEdit v-else :CustomerId="customer.Id"
                :Address="(selectedTemplate.type === typekeys.edit) ? address : null" :AddressList="addressList"
                @Created="e => setAddress(e, true)" @Changed="e => setAddress(e, true)" @Close="closeMainContent()">
              </AddressEdit>
            </template>

            <template v-if="selectedTemplate.identifier === identifierkeys.contact">
              <ContactList v-if="selectedTemplate.type === typekeys.list" :CustomerId="customer.Id" :Selected="contact"
                @New="selectedTemplate = { type: typekeys.new, identifier: identifierkeys.contact }"
                @Select="setContact" @Close="closeMainContent()">
              </ContactList>
              <ContactEdit v-else :CustomerId="customer.Id"
                :Contact="(selectedTemplate.type === typekeys.edit) ? contact : null" @Created="setContact"
                @Changed="setContact" @Close="closeMainContent()">
              </ContactEdit>
            </template>

            <template v-if="selectedTemplate.identifier === identifierkeys.job">
              <JobList :CustomerId="customer.Id" @Close="closeMainContent()">
              </JobList>
            </template>
          </template>
        </Layout>
      </template>
    </slotted-card>
    <CustomerJobsDialog :customer="customer" :externaltrigger="jobsdialogtrigger" />
  </v-card>

  <div v-else class="pl-4">
    {{ $t('servicesystem.common_objects.fallbackterms.no_customer') }}
  </div>
</template>


<script>
import clientAPI from "@/api/ClientAPIMixin.js";
import AddressEdit from "@/components/Preview/Customer/AddressEdit.vue";
import AddressList from "@/components/Preview/Customer/AddressList.vue";
import AddressTemplate from "@/components/Preview/Customer/AddressTemplate.vue";
import ContactEdit from "@/components/Preview/Customer/ContactEdit.vue";
import ContactList from "@/components/Preview/Customer/ContactList.vue";
import ContactTemplate from "@/components/Preview/Customer/ContactTemplate.vue";
import Layout from "@/views/job/newjob/cards/customer/CustomerCard2layout.vue";
import ParentCustomerCard from "@/views/job/newjob/cards/customer/ParentCustomerCard.vue";
//import JobListMini from "@/components/Preview/CustomerJobListView.vue";
import CustomerJobsDialog from "@/components/Dialogs/CustomerJobsDialog.vue";
import EditLi from "@/components/Inputs/EditableListItem.vue";
import JobList from "@/components/Preview/Customer/CustomerJobList.vue";
import EditBar from "@/components/Sections/Editbar.vue";
import SlottedCard from "@/views/layouts/SlottedCardSection.vue";
import WarningBanner from "@/components/WarningBanner.vue";

export default {
  name: "CustomerCard",
  props: {
    jobId: [Number, String],
    customer: Object,
    address: Object,
    contact: Object,
    isNew: Boolean,
    isOrder: Boolean,
    fillHeight: {
      type: Boolean,
      default: true
    }
  },
  components: {
    WarningBanner,
    ParentCustomerCard,
    Layout,
    AddressTemplate,
    AddressList,
    AddressEdit,
    ContactTemplate,
    ContactList,
    ContactEdit,
    //JobListMini,
    JobList,
    CustomerJobsDialog,
    SlottedCard,
    EditBar,
    EditLi,
  },
  mixins: [clientAPI],

  data() {
    return {
      selectedTemplate: null,
      typekeys: {
        list: "list",
        edit: "edit",
        new: "new",
      },
      identifierkeys: {
        contact: "contact",
        address: "address",
        job: "job",
      },

      editObject: {},
      copyObject: {},
      edittrigger: 0,
      editMode: false,
      valid: false,

      jobsdialogtrigger: 0,

      addressList: [],
      loading: false,
    };
  },

  mounted() {
    this.SetDetails();
    this.getAddresses();
  },

  methods: {
    async setAddress(newaddress, silenceSwap) {
      if (this.isOrder) {
        //not supported
      } else if (this.isNew) {
        this.$store.dispatch("JobHub/JobCustomer/setDeliveryAdress", newaddress);
      } else {
        if (silenceSwap) {
          this.$store.dispatch("JobHub/JobCustomer/setDeliveryAdress", newaddress);
        }
        await this.$store.dispatch("JobHub/JobCustomer/swapDeliveryAdress", newaddress);
      }
      this.closeMainContent();
    },

    getAddresses() {
      if (this.customer && this.customer.Id) {
        this.loading = true;
        this.getCustomerAdressesByCustomerId(this.customer.Id)
          .then((response) => {
            this.addressList = response;

          })
          .catch((response) => {
            console.error("getAddresses error", response);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },

    swapContact(newcontact) {
      this.$store.dispatch("JobHub/JobCustomer/swapCustomerContact", newcontact);
    },

    setContact(newcontact) {
      if (this.isOrder) {
        //console.log("Sent", this.customer.Id, newcontact.Id);
        this.SetInvoiceCustomer(this.jobId, this.customer.Id, newcontact.Id)
          .then((response) => {
            this.$store.dispatch("JobHub/JobParentjob/setJobOrderField", {
              key: "CustomerPersonId",
              value: response.CustomerPerson.Id,
            });
            //console.log("Recieved", response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.loading = false;
          });
      } else if (this.isNew) {
        this.$store.dispatch("JobHub/JobCustomer/setCustomerContact", newcontact);
      } else {
        this.$store.dispatch("JobHub/JobCustomer/swapCustomerContact", newcontact);
        this.$store.dispatch("JobHub/JobCustomer/setCustomerContact", newcontact);
      }
      this.closeMainContent();
    },

    closeMainContent() {
      this.selectedTemplate = null;
    },

    viewCustomerInNewTab() {
      let routeData = this.$router.resolve({ name: "ViewCustomer", params: { customerId: this.customer.Id } });
      window.open(routeData.href, "_blank");
    },

    setEditMode(bool) {
      this.editMode = bool;
    },

    setValid(bool) {
      this.valid = bool;
    },

    SetDetails() {
      var defaultobj = {
        Id: null,
        ParentId: null,
        CustomerGroupNo: "",
        Name: "",
        OrganisationNumber: "",
        CreditLimit: null,
        CreditLimitExceeded: null,
        FreeFromVat: false,
        Latitude: "",
        Longitude: "",
        IsActive: true,
        DefaultCustomerPerson: {},
        DefaultDeliveryAddress: {},
      };

      var existingcopy = Object.assign({}, this.customer);
      var obj = this.customer ? existingcopy : defaultobj;

      this.editObject = Object.assign({}, obj);
      this.copyObject = Object.assign({}, obj);
    },

    updateValue(update) {
      this.editObject[update.key] = update.value;
      //console.log("update", update, "result", this.editObject);
    },

    unfocusEntireCard() {
      if (!this.IsChanged && this.editMode) {
        this.edittrigger++;
      }
    },

    handleSave() {
      const dto = Object.assign({}, this.editObject);

      this.changeCustomerById(dto)
        .then((response) => {
          this.$store.dispatch("JobHub/JobCustomer/setCustomer", response);

          this.edittrigger++;
        })
        .catch((exception) => {
          console.error(exception);
          this.customer = null;
        });
    },

  },

  computed: {
    AllowChangeAddress() {
      var userAllowed = this.checkPermission("Actions", "editCustomerAddress");
      var id = this.address?.Id;
      var isDefault = this.addressIdIsDefaultForCustomer(id) || !id;
      var isExternal = this.addressIdIsFromErp(id);
      var isLocal = !isDefault && !isExternal;
      var isReadOnly = (isDefault && this.CustomerReadonly) || (isExternal && this.CustomerAddressReadonly) || (isLocal && this.CustomerAddressLocalReadonly);
      //console.log('AllowChangeAddress', userAllowed, this.address, id, isDefault, isExternal, isReadOnly);
      return userAllowed && !isReadOnly;
    },
    AllowSwapAddress() {
      return this.checkPermission("Actions", "swapJobCustomerAddress");
    },

    AllowChangeContact() {
      return !this.CustomerPersonsReadonly && this.checkPermission("Actions", "editCustomerContact");
    },
    AllowSwapContact() {
      return this.checkPermission("Actions", "swapJobCustomerContact");
    },

    IsChanged() {
      if (this.editObject && this.copyObject) {
        return !this.SameValue(this.editObject, this.copyObject);
      } else {
        return false;
      }
    },

    SwappingAddress() {
      return this.$store.getters['JobHub/JobCustomer/SwappingAddress'];
    },
    SwappingContact() {
      return this.$store.getters['JobHub/JobCustomer/SwappingContact'];
    },

    JobDetails() {
      return this.$store.getters["JobHub/JobParentjob/JobDetails"];
    },

    jobIsClosed() {
      return !(this.JobDetails && this.JobDetails.JobStatusId <= 70);
    },

    showInactiveCustomerWarning() {
      return this.customer && !this.customer.IsActive && this.JobDetails != null;
    },
    showCreditLimitExceededWarning() {
      if (this.jobIsClosed) {
        return false;
      }
      if (!this.customer || this.customer.CreditLimitExceeded == null) {
        return false;
      }
      return String(this.customer.CreditLimitExceeded).trim() !== '';
    },

    customerWarningMessages() {
      const messages = [];
      const t = (key) => this.$t('servicesystem.common_objects.customer.' + key);
      if (this.showInactiveCustomerWarning) {
        messages.push({
          severity: this.jobIsClosed ? 1 : 4,
          message: t('warning_inactive_customer'),
        });
      }
      if (this.showCreditLimitExceededWarning) {
        messages.push({
          severity: 3,
          message: t('warning_credit_limit_exceeded'),
        });
      }
      return messages;
    },
  },

  watch: {
    customer: function () {
      this.SetDetails();
    },
  },
};
</script>

<style lang="scss" scoped>
.q-input.h3 textarea {
  color: #5f5f5f !important;
}

.deactivated:not(:hover) {
  color: var(--v-disabled-darken2) !important;
  text-decoration: line-through !important;
}
</style>